import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const EditSeasonModal = ({ isOpen, season, contentItems, contentType = 'anime', onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    seasonNumber: '',
    posterUrl: '',
    releaseDate: '',
    year: '',
    contentId: '',
    contentType: contentType // Usa el contentType proporcionado
  });

  // Labels según el tipo de contenido
  const contentLabel = contentType === 'series' ? 'Serie' : 'Anime';

  // Cuando la temporada cambia, actualizar el formulario
  useEffect(() => {
    if (season) {
      console.log('Season data received:', season);
      // Formatear la fecha de lanzamiento si existe
      let formattedReleaseDate = '';
      if (season.releaseDate) {
        try {
          // Convertir la fecha al formato YYYY-MM-DD para el input type="date"
          const date = new Date(season.releaseDate);
          formattedReleaseDate = date.toISOString().split('T')[0];
        } catch (error) {
          console.error('Error formatting date:', error);
          formattedReleaseDate = '';
        }
      }

      // Manejar la inconsistencia entre 'poster' del backend y 'posterUrl' del frontend
      const posterImage = season.posterUrl || season.poster || '';
      console.log('Poster value found:', posterImage);

      const newFormData = {
        title: season.title || '',
        description: season.description || '',
        seasonNumber: season.seasonNumber?.toString() || '',
        posterUrl: posterImage,
        releaseDate: formattedReleaseDate,
        year: season.year?.toString() || '',
        contentId: season.contentId?.toString() || '',
        contentType: season.contentType || contentType
      };
      
      console.log('Setting form data:', newFormData);
      setFormData(newFormData);
    }
  }, [season, contentType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convertir tipos de datos antes de enviar
    const dataToSave = {
      ...formData,
      seasonNumber: parseInt(formData.seasonNumber, 10) || 1,
      year: parseInt(formData.year, 10) || new Date().getFullYear(),
      contentId: parseInt(formData.contentId, 10),
      contentType: contentType,
      // Asegurar que se envíen ambos campos poster y posterUrl para compatibilidad
      poster: formData.posterUrl,
    };
    
    console.log('Saving season data:', dataToSave);
    onSave({ id: season?.id, seasonData: dataToSave });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Editar Temporada</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white focus:outline-none"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="contentId" className="block text-sm font-medium text-gray-300">
              {contentLabel}
            </label>
            <select
              id="contentId"
              name="contentId"
              value={formData.contentId}
              onChange={handleChange}
              required
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            >
              <option value="">Selecciona {contentType === 'series' ? 'una serie' : 'un anime'}</option>
              {contentItems && contentItems.map(item => (
                <option key={item.id} value={item.id.toString()}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-300">
              Título
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="posterUrl" className="block text-sm font-medium text-gray-300">
              URL del Póster
            </label>
            <input
              type="text"
              id="posterUrl"
              name="posterUrl"
              value={formData.posterUrl}
              onChange={handleChange}
              placeholder="https://ejemplo.com/poster.jpg"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="seasonNumber" className="block text-sm font-medium text-gray-300">
                Número de Temporada
              </label>
              <input
                type="number"
                id="seasonNumber"
                name="seasonNumber"
                value={formData.seasonNumber}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-300">
                Año
              </label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                min={1900}
                max={new Date().getFullYear() + 5}
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>
          </div>

          <div>
            <label htmlFor="releaseDate" className="block text-sm font-medium text-gray-300">
              Fecha de Estreno
            </label>
            <input
              type="date"
              id="releaseDate"
              name="releaseDate"
              value={formData.releaseDate}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 border border-transparent rounded-md font-medium text-white hover:bg-blue-700"
            >
              Guardar Cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSeasonModal;
